import { DataService } from "@/services/data";
import { GraphView } from "@/types/dialer/graph-view";
import { VersionComparison } from "@/types/dialer/diff";
import { Scenario, ScenarioSummary } from "@/types/dialer/workflow";
import { handle_dialer_error } from "@/utils/dialer-errors";
import { handle_success } from "@/utils/wretch";
import { Navigate, redirect, RouteObject } from "react-router-dom";
import { loadBehaviorFields, loadLineage, loadNodeTypes, resolveVersion } from "./resolve";

async function loadView(versionId: number): Promise<GraphView> {
    return (await DataService.client.url(`/dialer/workflow-versions/${versionId}/view/`).get().json()) as GraphView;
}

/**
 * The canvas editor is **not** here. It lives outside the main layout, so its route is
 * declared in `routes/standalone/dialer-workflow-editor.tsx` beside the other screens
 * that need the whole window. Its URL is `/dialer-workflows/:id/edition`, aligned with
 * the SFLike detail; this tree only keeps the version / scenarios / compare satellites.
 */
const WorkflowRoute: RouteObject = {
    path: "workflows",
    children: [
        {
            // The workflow list is served by `sf_like` from `WorkflowConfig` now. Kept as
            // a redirect for the six breadcrumbs across the lineage, comparator, version
            // and simulator screens that name this URL.
            index: true,
            element: <Navigate to="/dialer-workflows" replace />
        },
        {
            // This legacy code-keyed URL resolves onto the workflow's SFLike Informations
            // tab. One read maps either `relance-dossier-principal` or `5` to the record id.
            //
            // The action below is left in place: it is this route's, the frieze submits
            // the discarding of a draft to it, and a fetcher posting here does not run
            // the loader.
            path: ":code",
            loader: async ({ params }) => {
                const lineage = await loadLineage(params.code as string);
                return redirect(`/dialer-workflows/${lineage.workflow.id}/informations`);
            },
            action: async ({ params, request }) => {
                const url = new URL(request.url);
                if (url.searchParams.get("action") === "discard-draft") {
                    const versionId = url.searchParams.get("version_id");
                    try {
                        await DataService.client.url(`/dialer/workflow-versions/${versionId}/`).delete().res();
                        return handle_success({ discarded: versionId });
                    } catch (error) {
                        return handle_dialer_error(error);
                    }
                }
                return handle_dialer_error(new Error(`Unknown workflow action on ${params.code}.`));
            },
            // Never rendered — the loader always answers with a redirect — but a route
            // needs something to fall back on if the resolution ever stops throwing.
            element: <Navigate to="/dialer-workflows" replace />
        },
        {
            path: ":code/v/:version",
            loader: async ({ params }) => {
                const { lineage, version } = await resolveVersion(params.code as string, params.version);
                const [view, nodeTypes, behaviorFields] = await Promise.all([loadView(version.id), loadNodeTypes(), loadBehaviorFields()]);
                return { view, nodeTypes, behaviorFields, lineage };
            },
            action: async ({ request }) => {
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                // The client sends the version id it is looking at, so a debounced dry run
                // costs one request rather than a resolution plus a request.
                const versionId = url.searchParams.get("version_id");
                const nodeId = url.searchParams.get("node_id");
                const data = await request.json();
                try {
                    if (action === "validate-node") {
                        return handle_success(await DataService.client.url(`/dialer/workflow-versions/${versionId}/nodes/${nodeId}/validate/`).post(data).json());
                    }
                    if (action === "save-node") {
                        return handle_success(await DataService.client.url(`/dialer/workflow-versions/${versionId}/nodes/${nodeId}/`).post(data).json());
                    }
                    if (action === "run-scenarios") {
                        return handle_success(await DataService.client.url(`/dialer/workflow-versions/${versionId}/run-scenarios/`).post(data).json());
                    }
                    return handle_dialer_error(new Error(`Unknown version action “${action}”.`));
                } catch (error) {
                    return handle_dialer_error(error);
                }
            },
            async lazy() {
                const { DialerWorkflowVersionPage } = await import("@/pages/dialer/workflows/version");
                return { Component: DialerWorkflowVersionPage };
            }
        },
        {
            path: ":code/v/:version/scenarios",
            loader: async ({ params }) => {
                const { version } = await resolveVersion(params.code as string, params.version);
                const [view, page] = await Promise.all([
                    loadView(version.id),
                    DataService.client.url(`/dialer/workflow-versions/${version.id}/scenarios/`).get().json() as Promise<{ scenarios: Scenario[]; summary: ScenarioSummary }>
                ]);
                return { version: view.version, scenarios: page.scenarios ?? [], summary: page.summary };
            },
            action: async ({ request }) => {
                const url = new URL(request.url);
                const versionId = url.searchParams.get("version_id");
                const scenarioId = url.searchParams.get("scenario_id");
                if (url.searchParams.get("action") !== "discard-carried-scenario") {
                    return handle_dialer_error(new Error("Unknown scenario action."));
                }
                try {
                    await DataService.client.url(`/dialer/workflow-versions/${versionId}/scenarios/${scenarioId}/`).delete().res();
                    return handle_success({ discarded: scenarioId });
                } catch (error) {
                    return handle_dialer_error(error);
                }
            },
            async lazy() {
                const { DialerWorkflowScenariosPage } = await import("@/pages/dialer/workflows/scenarios");
                return { Component: DialerWorkflowScenariosPage };
            }
        },
        {
            path: ":code/v/:version/comparer/:other",
            loader: async ({ params, request }) => {
                const { lineage, version: afterVersion } = await resolveVersion(params.code as string, params.version);
                const otherReference = Number(params.other);
                // Canonical URLs use business version numbers on both sides. Keep old
                // PK-based bookmarks working only when no such version number exists.
                const beforeVersion = lineage.versions.find(candidate => candidate.version === otherReference) ?? lineage.versions.find(candidate => candidate.id === otherReference);
                if (!beforeVersion) throw new Response(`Version ${otherReference} does not belong to workflow ${params.code}.`, { status: 404 });

                const comparison = (await DataService.client.url(`/dialer/workflow-versions/${beforeVersion.id}/compare/?to=${afterVersion.id}`).get().json()) as VersionComparison;
                const showGraphs = new URL(request.url).searchParams.get("tab") === "graphs";
                const [beforeView, afterView] = showGraphs ? await Promise.all([loadView(beforeVersion.id), loadView(afterVersion.id)]) : [null, null];
                return {
                    comparison,
                    beforeVersion,
                    afterVersion,
                    workflow: lineage.workflow,
                    beforeView,
                    afterView
                };
            },
            async lazy() {
                const { DialerWorkflowComparePage } = await import("@/pages/dialer/workflows/compare");
                return { Component: DialerWorkflowComparePage };
            }
        }
    ]
};

export default WorkflowRoute;
